/**
 * 99. Security Center — audit logs; sessions; API keys; anomaly alerts.
 *
 * Arabic and English (C7): every word from the catalogs; the tabs are chosen
 * by id, never by their words; severity and outcome are words. Event types
 * are codes and are shown as sent, left to right.
 *
 * Sessions (UI-HONEST): Revoke ends one of the caller's other sessions and
 * Revoke all ends every other one, each confirmed first, through
 * `POST /v1/security/sessions/:id/revoke` (the caller's own sessions only;
 * the revoked device gets 401 SESSION_REVOKED on its next call). The current
 * session is never offered: ending it is Sign out. Revoke all is shown only
 * when there is another session to end. The "Break-glass access" button was
 * removed: break-glass is staff access during an incident, approved inside
 * Atlas's operator console (operator-console.md section 3.5, OC-13), and the
 * customer's side of support access is Support access (OC-8a), which
 * replaces it.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Badge,
  Button,
  DataTable,
  Tabs,
  Skeleton,
} from "@/components/ui";
import { useConfirmedAction } from "@/components/ConfirmedAction";
import { post, useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { valueLabel } from "@/lib/i18n/records";
import { formatDateTime } from "@/lib/money";

interface SecurityEvent {
  id: string;
  eventType: string;
  severity: "Info" | "Warning" | "Critical";
  outcome: "Success" | "Failure" | "Blocked";
  actorId: string | null;
  sourceIp: string | null;
  occurredAt: string;
}

interface Session {
  id: string;
  device: string;
  sourceIp: string;
  lastActiveAt: string;
  current: boolean;
}

type Tab = "audit" | "sessions";
const TABS: Tab[] = ["audit", "sessions"];

export function SecurityCenterPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>("audit");
  const { data: events, isLoading: eventsLoading } = useApiList<SecurityEvent>(
    ["audit", "security"],
    "/audit/security",
  );
  const { data: sessions, isLoading: sessionsLoading } = useApiList<Session>(
    ["security", "sessions"],
    "/security/sessions",
  );
  const [notice, setNotice] = useState<string | null>(null);
  const others = (sessions?.items ?? []).filter((s) => !s.current);

  const revoke = useConfirmedAction({
    run: (session: Session) =>
      post(`/security/sessions/${session.id}/revoke`, {}),
    invalidate: [["security", "sessions"], ["me"]],
    onDone: () => setNotice(t("sessionControl.revoked")),
  });
  // One at a time, so a refusal names the session it stopped at; the list
  // is read again either way, so what ended is never shown as still open.
  const revokeAll = useConfirmedAction({
    run: async (ids: string[]) => {
      for (const id of ids) await post(`/security/sessions/${id}/revoke`, {});
      return ids.length;
    },
    invalidate: [["security", "sessions"], ["me"]],
    onDone: (count: number) =>
      setNotice(t("sessionControl.revokedAll", { count })),
  });

  return (
    <div>
      <PageHeader
        title={t("admin.security.title")}
        subtitle={t("admin.security.subtitle")}
      />
      <Tabs
        tabs={TABS.map((id) => ({
          id,
          label: t(`admin.security.tabs.${id}`),
        }))}
        active={tab}
        onChange={setTab}
      />

      {tab === "audit" && (
        <Card title={t("admin.security.events")}>
          {eventsLoading || !events ? (
            <Skeleton rows={6} />
          ) : (
            <DataTable<SecurityEvent>
              rows={events.items}
              columns={[
                {
                  key: "eventType",
                  label: t("fields.event"),
                  render: (r) => (
                    <span className="atlas-mono" dir="ltr">
                      {r.eventType}
                    </span>
                  ),
                },
                {
                  key: "severity",
                  label: t("fields.severity"),
                  render: (r) => (
                    <Badge
                      tone={
                        r.severity === "Critical"
                          ? "danger"
                          : r.severity === "Warning"
                            ? "warning"
                            : "default"
                      }
                    >
                      {valueLabel(t, "severity", r.severity)}
                    </Badge>
                  ),
                },
                {
                  key: "outcome",
                  label: t("fields.outcome"),
                  render: (r) => (
                    <Badge
                      tone={
                        r.outcome === "Success"
                          ? "success"
                          : r.outcome === "Blocked"
                            ? "warning"
                            : "danger"
                      }
                    >
                      {valueLabel(t, "outcome", r.outcome)}
                    </Badge>
                  ),
                },
                {
                  key: "sourceIp",
                  label: t("fields.ip"),
                  render: (r) => (
                    <span className="atlas-mono">{r.sourceIp ?? "—"}</span>
                  ),
                },
                {
                  key: "occurredAt",
                  label: t("admin.security.time"),
                  render: (r) => formatDateTime(r.occurredAt),
                },
              ]}
            />
          )}
        </Card>
      )}

      {tab === "sessions" && (
        <Card
          title={t("admin.security.sessionsTitle")}
          actions={
            others.length > 0 ? (
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  setNotice(null);
                  revokeAll.ask(
                    others.map((s) => s.id),
                    {
                      title: t("sessionControl.revokeAllTitle"),
                      body: t("sessionControl.revokeAllBody", {
                        count: others.length,
                      }),
                      confirmLabel: t("sessionControl.revokeAllConfirm"),
                    },
                  );
                }}
              >
                {t("admin.security.revokeAll")}
              </Button>
            ) : undefined
          }
        >
          {notice && (
            <p role="status" style={{ marginBlock: "0 12px" }}>
              {notice}
            </p>
          )}
          {revoke.dialog}
          {revokeAll.dialog}
          {sessionsLoading || !sessions ? (
            <Skeleton rows={5} />
          ) : (
            <DataTable<Session>
              rows={sessions.items}
              columns={[
                {
                  key: "device",
                  label: t("fields.device"),
                  render: (r) => <span dir="ltr">{r.device}</span>,
                },
                {
                  key: "sourceIp",
                  label: t("fields.ip"),
                  render: (r) => (
                    <span className="atlas-mono">{r.sourceIp}</span>
                  ),
                },
                {
                  key: "lastActiveAt",
                  label: t("fields.lastActive"),
                  render: (r) => formatDateTime(r.lastActiveAt),
                },
                {
                  key: "current",
                  label: t("fields.status"),
                  render: (r) =>
                    r.current ? (
                      <Badge tone="primary">
                        {t("admin.security.thisSession")}
                      </Badge>
                    ) : (
                      <Badge tone="default">{t("fields.active")}</Badge>
                    ),
                },
                {
                  key: "id",
                  label: t("fields.actions"),
                  render: (r) =>
                    r.current ? (
                      "—"
                    ) : (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          setNotice(null);
                          revoke.ask(r, {
                            title: t("sessionControl.revokeTitle"),
                            body: t("sessionControl.revokeBody", {
                              device: r.device,
                            }),
                            confirmLabel: t("sessionControl.revokeConfirm"),
                          });
                        }}
                      >
                        {t("admin.security.revoke")}
                      </Button>
                    ),
                },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
}
